// Shaped procedural stand-ins for placeable items.
//
// None of the 19 structure sprites in gen-me.md §2 exist yet, and drawing a
// coop, a waterer and a fence panel as the same extruded cube left the player
// unable to tell what they had built. These draw a recognisable isometric
// silhouette per item out of flat clay tones. worldRenderer prefers a real
// sprite the moment one lands, so nothing here has to be unwound later.
//
// Everything is drawn in footprint space: (fx, fy) in tiles measured from the
// centre of the item's footprint, `up` in pixels above the ground plane.

import { Graphics } from 'pixi.js';
import { TILE_DEPTH, TILE_HEIGHT, TILE_WIDTH } from './iso';
import { CLAY, shade } from './palette';

const HW = TILE_WIDTH / 2;
const HH = TILE_HEIGHT / 2;
/** Vertical pixels per elevation unit — one unit is about knee height. */
const UNIT = TILE_DEPTH;

// Key light is upper-left (matches the clay renders), so the face pointing
// screen-left-down is lit and the one pointing screen-right-down is in shade.
const LIT = 0.1;
const SHADED = 0.32;

const C = {
  wood: 0xb5703c,
  woodDark: 0x8a5227,
  roof: 0x9c4f34,
  paleWood: 0xc9a06a,
  straw: 0xd8bd7f,
  metal: 0x9aa0a2,
  water: 0x6fa3c9,
  waterPale: 0xa8cfe4,
  feed: 0xd9b877,
  feedPale: 0xefdcb4,
  leaf: 0x6f9a52,
  fabric: 0x63a08a,
  glow: 0xf7c96b,
  terracotta: 0xb4643f,
} as const;

interface P {
  x: number;
  y: number;
}

function p(fx: number, fy: number, up = 0): P {
  return { x: (fx - fy) * HW, y: (fx + fy) * HH - up };
}

function poly(g: Graphics, color: number, pts: P[]): void {
  g.beginFill(color);
  g.drawPolygon(pts.flatMap((q) => [q.x, q.y]));
  g.endFill();
}

/** Soft contact shadow. `r` is a radius in tiles. */
function ground(g: Graphics, r: number): void {
  g.beginFill(CLAY.shadow, 0.22);
  g.drawEllipse(0, 0, r * HW, r * HH);
  g.endFill();
}

/** Box with its base centred at (cx, cy), `h` pixels tall. */
function box(
  g: Graphics,
  cx: number,
  cy: number,
  ax: number,
  ay: number,
  base: number,
  h: number,
  color: number,
  topColor = color,
): void {
  const top = base + h;
  const n = p(cx - ax, cy - ay, top);
  const e = p(cx + ax, cy - ay, top);
  const s = p(cx + ax, cy + ay, top);
  const w = p(cx - ax, cy + ay, top);
  poly(g, shade(color, SHADED), [e, s, p(cx + ax, cy + ay, base), p(cx + ax, cy - ay, base)]);
  poly(g, shade(color, LIT), [w, s, p(cx + ax, cy + ay, base), p(cx - ax, cy + ay, base)]);
  poly(g, topColor, [n, e, s, w]);
}

/** Upright cylinder, base centred at (cx, cy). `r` is a radius in tiles. */
function cylinder(
  g: Graphics,
  cx: number,
  cy: number,
  r: number,
  base: number,
  h: number,
  color: number,
): void {
  const c = p(cx, cy, base);
  const rx = r * HW;
  const ry = r * HH;
  g.beginFill(shade(color, SHADED));
  g.drawEllipse(c.x, c.y, rx, ry);
  g.endFill();
  // Two flat bands stand in for a curved shade — cheap and reads correctly.
  const split = c.x + rx * 0.2;
  poly(g, shade(color, LIT), [
    { x: c.x - rx, y: c.y },
    { x: split, y: c.y },
    { x: split, y: c.y - h },
    { x: c.x - rx, y: c.y - h },
  ]);
  poly(g, shade(color, SHADED), [
    { x: split, y: c.y },
    { x: c.x + rx, y: c.y },
    { x: c.x + rx, y: c.y - h },
    { x: split, y: c.y - h },
  ]);
  g.beginFill(color);
  g.drawEllipse(c.x, c.y - h, rx, ry);
  g.endFill();
}

/** Pitched roof with its ridge running along the fx axis. */
function roof(
  g: Graphics,
  ax: number,
  ay: number,
  eave: number,
  rise: number,
  color: number,
): void {
  const peak = eave + rise;
  const ridgeBack = p(-ax, 0, peak);
  const ridgeFront = p(ax, 0, peak);
  // Gable end on the shaded side, then the big lit slope over the top of it.
  poly(g, shade(color, SHADED + 0.06), [p(ax, -ay, eave), ridgeFront, p(ax, ay, eave)]);
  poly(g, shade(color, LIT), [ridgeBack, ridgeFront, p(ax, ay, eave), p(-ax, ay, eave)]);
  g.lineStyle({ width: 2, color: shade(color, 0.45), alpha: 0.7 });
  g.moveTo(ridgeBack.x, ridgeBack.y);
  g.lineTo(ridgeFront.x, ridgeFront.y);
  g.lineStyle(0);
}

/** Thin upright, for posts, poles and legs. */
function post(g: Graphics, cx: number, cy: number, h: number, color: number, r = 0.045): void {
  box(g, cx, cy, r, r, 0, h, color);
}

/**
 * Open-topped container: floor inside a ring of walls. A solid box with a
 * coloured top face reads as a flat slab from this camera angle — the walls
 * have to be separate geometry with the floor sunk between them.
 */
function pen(
  g: Graphics,
  ax: number,
  ay: number,
  h: number,
  wall: number,
  floor: number,
): void {
  const t = 0.07;
  // Back walls, then the floor over their inner edge, then the near walls.
  box(g, 0, -ay, ax, t, 0, h, wall);
  box(g, -ax, 0, t, ay, 0, h, wall);
  box(g, 0, 0, ax, ay, 0, h * 0.24, floor);
  box(g, 0, ay, ax, t, 0, h, wall);
  box(g, ax, 0, t, ay, 0, h, wall);
}

/** Dark recess on the lit (+fy) face — doorways and nest openings. */
function opening(g: Graphics, ay: number, from: number, to: number, lo: number, hi: number): void {
  poly(g, shade(CLAY.outline, 0.1), [
    p(from, ay, hi),
    p(to, ay, hi),
    p(to, ay, lo),
    p(from, ay, lo),
  ]);
}

// --- per-item shapes -------------------------------------------------------

type Shape = (g: Graphics, ax: number, ay: number) => void;

function coop(g: Graphics, ax: number, ay: number): void {
  ground(g, (ax + ay) * 0.85);
  const wx = ax * 0.82;
  const wy = ay * 0.82;
  const wallH = 1.1 * UNIT;
  box(g, 0, 0, wx, wy, 0, wallH, C.wood);
  opening(g, wy, -wx * 0.1, wx * 0.5, 0, wallH * 0.55);
  roof(g, wx * 1.16, wy * 1.16, wallH, 0.85 * UNIT, C.roof);
}

function quarantinePen(g: Graphics, ax: number, ay: number): void {
  ground(g, (ax + ay) * 0.8);
  const wx = ax * 0.86;
  const wy = ay * 0.86;
  pen(g, wx, wy, 0.55 * UNIT, C.paleWood, shade(C.straw, 0.3));
  // Corner posts run past the wall top, so it reads as isolation and not storage.
  for (const [sx, sy] of [
    [-1, -1],
    [1, -1],
    [1, 1],
    [-1, 1],
  ]) {
    post(g, wx * sx, wy * sy, 1.1 * UNIT, C.woodDark, 0.055);
  }
}

function brooder(g: Graphics, ax: number, ay: number): void {
  ground(g, (ax + ay) * 0.8);
  const wx = ax * 0.82;
  const wy = ay * 0.82;
  pen(g, wx, wy, 0.6 * UNIT, C.paleWood, shade(C.straw, 0.08));
  // Warm pool on the litter, under the lamp rather than under the whole box.
  g.beginFill(C.glow, 0.2);
  g.drawEllipse(0, 0.14 * UNIT, 40, 20);
  g.endFill();
  // Heat lamp on a gooseneck reaching in from the back corner.
  const stand = 2.3 * UNIT;
  post(g, -wx * 0.85, -wy * 0.85, stand, C.metal, 0.04);
  const armFrom = p(-wx * 0.85, -wy * 0.85, stand);
  const hang = p(0, 0, 1.5 * UNIT);
  g.lineStyle({ width: 4, color: shade(C.metal, 0.25) });
  g.moveTo(armFrom.x, armFrom.y);
  g.quadraticCurveTo(armFrom.x, hang.y - 26, hang.x, hang.y - 22);
  g.lineStyle(0);
  poly(g, shade(C.terracotta, SHADED), [
    { x: hang.x - 22, y: hang.y },
    { x: hang.x + 22, y: hang.y },
    { x: hang.x + 10, y: hang.y - 21 },
    { x: hang.x - 10, y: hang.y - 21 },
  ]);
  g.beginFill(C.glow);
  g.drawEllipse(hang.x, hang.y + 2, 18, 7);
  g.endFill();
}

function nestingBox(g: Graphics, ax: number, ay: number): void {
  ground(g, (ax + ay) * 0.6);
  const wx = ax * 0.6;
  const wy = ay * 0.6;
  const h = 0.62 * UNIT;
  box(g, 0, 0, wx, wy, 0, h, C.wood);
  opening(g, wy, -wx * 0.55, wx * 0.55, h * 0.2, h * 0.85);
  // Straw spilling over the lip, so it reads as a nest and not a crate.
  g.beginFill(C.straw);
  const lip = p(0, wy, h * 0.24);
  g.drawEllipse(lip.x, lip.y, 13, 4);
  g.endFill();
}

function perchBar(g: Graphics, ax: number, ay: number): void {
  ground(g, (ax + ay) * 0.5);
  const legH = 0.72 * UNIT;
  post(g, -ax * 0.72, 0, legH, C.woodDark, 0.05);
  post(g, ax * 0.72, 0, legH, C.woodDark, 0.05);
  box(g, 0, 0, ax * 0.88, 0.055, legH, 0.1 * UNIT, C.wood);
}

/** Chick fount: wide shallow saucer, small jar. Deliberately unlike the adult. */
function chickWaterer(g: Graphics): void {
  ground(g, 0.34);
  cylinder(g, 0, 0, 0.3, 0, 0.1 * UNIT, C.waterPale);
  cylinder(g, 0, 0, 0.15, 0.1 * UNIT, 0.34 * UNIT, C.water);
}

/** Adult waterer: tall hopper on a narrow foot, with a carry handle. */
function adultWaterer(g: Graphics): void {
  ground(g, 0.3);
  cylinder(g, 0, 0, 0.26, 0, 0.12 * UNIT, C.waterPale);
  cylinder(g, 0, 0, 0.19, 0.12 * UNIT, 0.8 * UNIT, C.water);
  const top = p(0, 0, 0.92 * UNIT);
  g.lineStyle({ width: 3, color: shade(C.metal, 0.2) });
  g.moveTo(top.x - 11, top.y);
  g.quadraticCurveTo(top.x, top.y - 16, top.x + 11, top.y);
  g.lineStyle(0);
}

/** Chick tray: almost flat, with a perforated lid the chicks feed through. */
function chickFeeder(g: Graphics): void {
  ground(g, 0.34);
  cylinder(g, 0, 0, 0.3, 0, 0.09 * UNIT, C.feedPale);
  const top = p(0, 0, 0.09 * UNIT);
  g.beginFill(shade(C.feedPale, 0.4));
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    g.drawEllipse(top.x + Math.cos(a) * 13, top.y + Math.sin(a) * 6.5, 3.5, 2);
  }
  g.endFill();
}

/** Adult dish: deeper, raised on a foot, feed visible in it. */
function adultFeeder(g: Graphics): void {
  ground(g, 0.3);
  cylinder(g, 0, 0, 0.14, 0, 0.1 * UNIT, shade(C.feedPale, 0.2));
  cylinder(g, 0, 0, 0.28, 0.1 * UNIT, 0.2 * UNIT, C.feedPale);
  const top = p(0, 0, 0.3 * UNIT);
  g.beginFill(C.feed);
  g.drawEllipse(top.x, top.y, 0.22 * HW, 0.22 * HH);
  g.endFill();
}

function coveredFeeder(g: Graphics): void {
  adultFeeder(g);
  post(g, 0, -0.16, 0.95 * UNIT, C.metal, 0.03);
  const hood = p(0, 0, 0.78 * UNIT);
  poly(g, shade(C.roof, LIT), [
    { x: hood.x - 26, y: hood.y },
    { x: hood.x, y: hood.y + 13 },
    { x: hood.x + 26, y: hood.y },
    { x: hood.x, y: hood.y - 13 },
  ]);
  poly(g, shade(C.roof, SHADED), [
    { x: hood.x - 26, y: hood.y },
    { x: hood.x, y: hood.y + 13 },
    { x: hood.x + 26, y: hood.y },
    { x: hood.x, y: hood.y + 20 },
  ]);
}

function footbath(g: Graphics): void {
  ground(g, 0.38);
  box(g, 0, 0, 0.36, 0.36, 0, 0.08 * UNIT, C.metal);
  // Disinfectant surface, inset so the tray rim still reads.
  const top = p(0, 0, 0.08 * UNIT);
  poly(g, 0x8fc4c0, [
    { x: top.x, y: top.y - 0.29 * TILE_HEIGHT * 0.5 },
    { x: top.x + 0.29 * TILE_WIDTH * 0.5, y: top.y },
    { x: top.x, y: top.y + 0.29 * TILE_HEIGHT * 0.5 },
    { x: top.x - 0.29 * TILE_WIDTH * 0.5, y: top.y },
  ]);
}

function waterBarrel(g: Graphics): void {
  ground(g, 0.32);
  cylinder(g, 0, 0, 0.29, 0, 1.05 * UNIT, C.water);
  const lid = p(0, 0, 1.05 * UNIT);
  g.lineStyle({ width: 2, color: shade(C.water, 0.4), alpha: 0.8 });
  g.drawEllipse(lid.x, lid.y, 0.29 * HW * 0.72, 0.29 * HH * 0.72);
  g.lineStyle(0);
  // Tap low on the lit face.
  box(g, 0, 0.26, 0.04, 0.04, 0.18 * UNIT, 0.1 * UNIT, C.metal);
}

function shadeSail(g: Graphics, ax: number, ay: number): void {
  ground(g, (ax + ay) * 0.7);
  const px = ax * 0.86;
  const py = ay * 0.86;
  const top = 1.5 * UNIT;
  const corners: Array<[number, number]> = [
    [-px, -py],
    [px, -py],
    [px, py],
    [-px, py],
  ];
  for (const [cx, cy] of corners) post(g, cx, cy, top, C.metal, 0.04);
  // Fanned from a low centre point so the fabric sags instead of reading flat.
  const mid = p(0, 0, top - 0.22 * UNIT);
  for (let i = 0; i < 4; i++) {
    const a = p(corners[i][0], corners[i][1], top);
    const b = p(corners[(i + 1) % 4][0], corners[(i + 1) % 4][1], top);
    poly(g, shade(C.fabric, i === 2 ? LIT : SHADED), [a, b, mid]);
  }
}

/**
 * Half-inch hardware cloth skirted around the base of the run (REFOCUS §7). It
 * is the perimeter defense the coop ladder used to grant, so it has to read as
 * a barrier the birds and a digger both meet: short posts with a mesh panel
 * strung between them, not a fence tall enough to look like the old one.
 */
function hardwareCloth(g: Graphics, ax: number, ay: number): void {
  ground(g, (ax + ay) * 0.8);
  const px = ax * 0.9;
  const py = ay * 0.9;
  const top = 0.5 * UNIT;
  const corners: Array<[number, number]> = [
    [-px, -py],
    [px, -py],
    [px, py],
    [-px, py],
  ];
  for (const [cx, cy] of corners) post(g, cx, cy, top, C.metal, 0.035);
  // The near two panels are solid mesh; the far two are drawn lighter so the
  // run still reads as an open yard seen through its own perimeter.
  for (let i = 0; i < 4; i++) {
    const a = corners[i];
    const b = corners[(i + 1) % 4];
    const near = i >= 2;
    poly(g, shade(C.metal, near ? SHADED : LIT), [
      p(a[0], a[1], top),
      p(b[0], b[1], top),
      p(b[0], b[1]),
      p(a[0], a[1]),
    ]);
  }
}

function decorFlag(g: Graphics): void {
  ground(g, 0.16);
  const h = 1.5 * UNIT;
  post(g, 0, 0, h, C.paleWood, 0.028);
  const tip = p(0, 0, h);
  poly(g, CLAY.accent, [
    { x: tip.x + 2, y: tip.y + 2 },
    { x: tip.x + 30, y: tip.y + 11 },
    { x: tip.x + 2, y: tip.y + 20 },
  ]);
}

function decorPlanter(g: Graphics): void {
  ground(g, 0.3);
  box(g, 0, 0, 0.26, 0.26, 0, 0.42 * UNIT, C.terracotta, shade(CLAY.dirt, 0.15));
  const soil = p(0, 0, 0.42 * UNIT);
  g.beginFill(C.leaf);
  for (const [dx, dy, r] of [
    [-9, -2, 8],
    [8, 0, 7],
    [0, -7, 6],
  ]) {
    g.drawEllipse(soil.x + dx, soil.y + dy - 4, r, r * 0.75);
  }
  g.endFill();
}

function coopLight(g: Graphics): void {
  ground(g, 0.2);
  const h = 1.9 * UNIT;
  post(g, -0.3, -0.3, h, C.metal, 0.04);
  const arm = p(-0.3, -0.3, h);
  const lamp = p(0.08, 0.08, h - 0.3 * UNIT);
  g.lineStyle({ width: 4, color: shade(C.metal, 0.25) });
  g.moveTo(arm.x, arm.y);
  g.quadraticCurveTo(arm.x, lamp.y - 20, lamp.x, lamp.y - 17);
  g.lineStyle(0);
  poly(g, shade(C.metal, SHADED), [
    { x: lamp.x - 19, y: lamp.y },
    { x: lamp.x + 19, y: lamp.y },
    { x: lamp.x + 9, y: lamp.y - 16 },
    { x: lamp.x - 9, y: lamp.y - 16 },
  ]);
  g.beginFill(C.glow);
  g.drawEllipse(lamp.x, lamp.y + 2, 15, 6);
  g.endFill();
  g.beginFill(C.glow, 0.18);
  g.drawEllipse(lamp.x, lamp.y + 16, 26, 13);
  g.endFill();
}

/** Auto-feeder: a dish with a hopper mounted on top. */
function autoFeeder(g: Graphics): void {
  adultFeeder(g);
  cylinder(g, 0, 0, 0.18, 0.3 * UNIT, 0.5 * UNIT, shade(C.metal, 0.1));
  const top = p(0, 0, 0.8 * UNIT);
  g.beginFill(shade(C.metal, 0.2));
  g.drawEllipse(top.x, top.y, 0.16 * HW, 0.16 * HH);
  g.endFill();
}

/** Auto-waterer: a hopper with a drip nipple mounted on top. */
function autoWaterer(g: Graphics): void {
  adultWaterer(g);
  cylinder(g, 0, 0, 0.12, 0.92 * UNIT, 0.4 * UNIT, shade(C.metal, 0.05));
  box(g, 0, 0.1, 0.04, 0.04, 1.0 * UNIT, 0.18 * UNIT, C.metal);
}

/** Coop lock-timer: a small control box with a dial. */
function coopLockTimer(g: Graphics): void {
  ground(g, 0.22);
  box(g, 0, 0, 0.2, 0.16, 0, 0.45 * UNIT, shade(C.metal, 0.05));
  const face = p(0, 0.16, 0.3 * UNIT);
  g.beginFill(shade(C.waterPale, 0.1));
  g.drawEllipse(face.x, face.y, 8, 8);
  g.endFill();
  g.lineStyle({ width: 2, color: shade(C.metal, 0.4) });
  g.moveTo(face.x, face.y);
  g.lineTo(face.x + 4, face.y - 5);
  g.lineStyle(0);
}

/** Anything without a shape yet still gets the old extruded cube. */
function fallbackBox(g: Graphics, ax: number, ay: number): void {
  ground(g, (ax + ay) * 0.8);
  box(g, 0, 0, ax * 0.8, ay * 0.8, 0, 0.5 * UNIT, C.paleWood);
}

const SHAPES: Record<string, Shape> = {
  coop_l1: coop,
  coop_l2: coop,
  coop_master: coop,
  quarantine_pen: quarantinePen,
  brooder_box: brooder,
  nesting_box: nestingBox,
  perch_bar: perchBar,
  chick_waterer: chickWaterer,
  adult_waterer: adultWaterer,
  chick_feeder: chickFeeder,
  adult_feeder: adultFeeder,
  covered_feeder: coveredFeeder,
  footbath: footbath,
  water_barrel: waterBarrel,
  shade_sail: shadeSail,
  // Netting reads as the same overhead plane as a shade sail until its own art
  // lands; both are a stretched cover over the run.
  run_netting: shadeSail,
  hardware_cloth: hardwareCloth,
  decor_flag: decorFlag,
  decor_planter: decorPlanter,
  coop_light: coopLight,
  auto_feeder: autoFeeder,
  auto_waterer: autoWaterer,
  coop_lock_timer: coopLockTimer,
};

export function hasShape(itemId: string): boolean {
  return itemId in SHAPES;
}

/**
 * Draws `itemId` into `g`, centred on its footprint. The caller positions the
 * Graphics at the footprint centre in world space, so the two halves agree with
 * the sprite path, which anchors real art the same way.
 */
export function drawStructure(g: Graphics, itemId: string, footW = 1, footH = 1): Graphics {
  g.clear();
  (SHAPES[itemId] ?? fallbackBox)(g, footW / 2, footH / 2);
  return g;
}
